import React, { useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import moment from 'moment';
import {
  FlexBox,
  PrimaryButton,
  DoconInput,
  DoconDatePicker,
  DoconLabel,
  DoconRadioButton,
  message,
} from '@docon/docon-ui-library';
import { createNewPatient, updateAuthState, updateUser } from 'redux/reducer/auth';

interface IProps {
  doctor: any;
  user: any;
  onSuccess: () => void;
}

enum Labels {
  firstName = 'First Name*',
  lastName = 'Last Name',
  dateOfBirth = 'Date of Birth*',
  sex = 'Gender*',
}

interface IForm {
  firstName: string;
  lastName: string;
  dateOfBirth: any;
  sex: string;
}

const Root = styled(FlexBox)`
  padding: 1.5rem;

  > div {
    margin-bottom: 12px;
  }

  .dateInputRoot {
    width: calc(50% - 12px);

    ${DoconDatePicker} {
      width: 100%;
    }
  }

  .radioButton {
    margin-top: auto;
    margin-bottom: auto;
    gap: 12px;
    --space: 0;
  }

  ${PrimaryButton} {
    margin-top: 48px;
    font-size: 18px;
    font-weight: bold;
  }

  ${() => isMobileOnly && css`
    padding: 1rem;
  `}
`;

function AddPatient({
  doctor,
  user,
  onSuccess,
}: IProps) {

  const [form, setForm] = useState<IForm>({
    firstName: '',
    lastName: '',
    dateOfBirth: moment(),
    sex: 'Male',
  });

  const dispatch = useDispatch();

  const {
    patientCreating,
    createdPatientError,
    patientCreated,
  } = useSelector(state => state.auth);

  useEffect(() => {
    return () => {
      if (createdPatientError)
        dispatch(updateAuthState({ createdPatientError: '' }));
    };
  }, []);

  useEffect(() => {
    if (createdPatientError) {
      message.error(createdPatientError);
    }
  }, [createdPatientError]);

  useEffect(() => {
    if (patientCreated) {
      onSuccess();

      if (!user.firstName)
        dispatch(updateUser({
          userID: user.userID,
          mobileNumber: user.mobileNumber,
          gender: form.sex,
          firstName: form.firstName,
          lastName: form.firstName,
          dateOfBirth: form.dateOfBirth,
        }));

      dispatch(updateAuthState({
        patientCreated: false,
      }));
    }
  }, [patientCreated]);

  function commonInputProps(name: keyof typeof Labels) {
    return {
      key: name,
      name,
      label: Labels[name],
      value: form[name],
      enableValueSanitize: true,
      hideCrossIcon: true,
      onChange: handleChange,
      onBlur: handleChange,
    };
  }

  function handleChange({ name, value }: any) {
    setForm({ ...form, [name]: value });
  }

  function handleBtnClick() {
    dispatch(createNewPatient({
      ownerID: doctor.doctorID,
      mobileNumber: user.mobileNumber,
      ...form,
    }));
  }

  return (
    <Root>
      <DoconInput
        autoFocus
        {...commonInputProps('firstName')}
      />

      <DoconInput
        {...commonInputProps('lastName')}
      />

      <FlexBox flex={'100%'} gap={isMobileOnly ? '16px' : '24px'}>
        <DoconDatePicker
          disableFuture
          format={'DD/MM/yyyy'}
          placeholder='dd/mm/yyyy'
          inputReadOnly
          rootClassName='dateInputRoot'
          {...commonInputProps('dateOfBirth')}
        />

        <FlexBox vertical verticalSpace={8}>
          <DoconLabel label='Gender*' />
          <DoconRadioButton
            vertical={false}
            options={[
              { label: 'Male', value: 'Male' },
              { label: 'Female', value: 'Female' },
            ]}
            className='radioButton'
            selected={form.sex}
            {...commonInputProps('sex')}
          />
        </FlexBox>
      </FlexBox>

      <PrimaryButton
        isBlock
        icon={patientCreating ? 'loading' : ''}
        disabled={!(form.dateOfBirth && form.sex && form.firstName)}
        title={'CREATE'}
        onClick={handleBtnClick}
      />
    </Root>
  );
}

export default AddPatient;